import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Compass, Users, CreditCard, IdCard, FileText, LogOut, Menu, X, LayoutDashboard, Bell, Inbox, GraduationCap } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const PARENT_NAV = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Explore", to: "/experiences", icon: Compass },
  { label: "My Students", to: "/students", icon: Users },
  { label: "Membership", to: "/membership", icon: IdCard },
  { label: "Forms", to: "/forms", icon: FileText },
  { label: "Payments", to: "/payments", icon: CreditCard },
];

const ADMIN_NAV = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Experiences", to: "/admin/experiences", icon: Compass },
  { label: "Check-In", to: "/admin/checkin", icon: Users },
  { label: "Inquiries", to: "/admin/inquiries", icon: Inbox },
  { label: "Scholarships", to: "/admin/scholarship", icon: GraduationCap },
  { label: "Forms & Documents", to: "/admin/forms", icon: FileText },
  { label: "Finances", to: "/admin/finances", icon: CreditCard },
  { label: "Content", to: "/admin/content", icon: FileText },
  { label: "Announcements", to: "/admin/announcements", icon: Bell },
];

export default function AppShell({ children, admin = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const nav = admin ? ADMIN_NAV : PARENT_NAV;

  const doLogout = () => { logout(); navigate("/"); };

  const NavLinks = ({ onClick }) => (
    <>
      {nav.map((n) => {
        const active = pathname === n.to;
        const Icon = n.icon;
        return (
          <Link key={n.to} to={n.to} onClick={onClick}
            data-testid={`app-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 font-display text-sm font-semibold transition-colors ${active ? "bg-brandblue text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
            <Icon className="h-4 w-4" />{n.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-cloud">
      {/* Sidebar - desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-navy px-4 py-6 lg:flex">
        <Logo dark />
        <nav className="mt-10 flex flex-1 flex-col gap-1"><NavLinks /></nav>
        <div className="border-t border-white/10 pt-4">
          <div className="px-3 pb-3 text-xs text-white/50">{user?.name}<br /><span className="capitalize">{user?.role?.replace("_", " ")}</span></div>
          <button onClick={doLogout} data-testid="app-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-display text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" />Sign Out
          </button>
        </div>
      </aside>

      {/* Topbar - mobile */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-navy px-4 py-3 lg:hidden">
        <Logo dark subtitle={false} />
        <button onClick={() => setOpen(!open)} data-testid="app-mobile-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
        </button>
      </div>
      {open && (
        <div className="fixed inset-0 z-30 bg-navy px-4 pt-20 lg:hidden">
          <nav className="flex flex-col gap-1"><NavLinks onClick={() => setOpen(false)} /></nav>
          <button onClick={doLogout} className="mt-4 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-display text-sm font-semibold text-white/70 hover:bg-white/10">
            <LogOut className="h-4 w-4" />Sign Out
          </button>
        </div>
      )}

      <div className="lg:pl-64">
        <main className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-10">{children}</main>
      </div>
    </div>
  );
}
